import type { ActionOutputV2Payload } from '@circuitry-ai/doc-ui';
import { getActionOutputV2Payload } from '@circuitry-ai/doc-ui';

export type AdjustmentType = 'part' | 'labor' | string;

export type AdjustmentLinkEvent = {
    type?: string;
    eventType?: string;
    agentCode?: string;
    agentName?: string;
    claimNumber?: string;
    column?: string;
    columnName?: string;
    isSimCheck?: boolean;
    data?: {
        adjustmentType?: AdjustmentType;
        failureId?: string | number;
        detailId?: string | number;
        simData?: Record<string, unknown>;
    };
};

export type TagAgentRun = {
    agent_name?: string;
    entity_number?: string;
    action_output?: unknown;
    timestamp?: string;
    [key: string]: unknown;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
    value != null && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;

const pickExplanation = (node: unknown): Record<string, unknown> | null => {
    const rec = asRecord(node);
    if (!rec) return null;
    return asRecord(rec.explanation) ?? rec;
};

const findKey = (rec: Record<string, unknown> | null, ...candidates: string[]): unknown => {
    if (!rec) return undefined;
    for (const key of candidates) {
        if (key in rec) return rec[key];
    }
    const lower = Object.keys(rec);
    for (const candidate of candidates) {
        const match = lower.find((k) => k.toLowerCase() === candidate.toLowerCase() || k.toLowerCase().startsWith(`${candidate.toLowerCase()}@`));
        if (match) return rec[match];
    }
    return undefined;
};

export const isAdjustmentLinkClicked = (event: AdjustmentLinkEvent | null | undefined): boolean =>
    event?.type === 'adjustmentLinkClicked' || event?.eventType === 'adjustmentDetails';

export const normalizeAgentName = (name?: string): string =>
    (name ?? '').replace(/_/g, ' ').trim().toLowerCase();

export const findAgentRunByName = (runs: unknown, agentName?: string): TagAgentRun | null => {
    const list = Array.isArray(runs) ? runs : asRecord(runs)?.data;
    if (!Array.isArray(list) || !agentName) return null;
    const wanted = normalizeAgentName(agentName);
    return (
        list.find((item) => normalizeAgentName(asRecord(item)?.agent_name as string) === wanted) ??
        null
    );
};

export const getRequestExplanation = (
    payload: ActionOutputV2Payload | null,
    failureId: string | number | undefined
): Record<string, unknown> | null => {
    if (!payload || failureId == null) return null;
    const requests = pickExplanation(payload.explanation?.requests);
    return pickExplanation(findKey(requests, String(failureId)));
};

export type AdjustmentContentKind = 'conversation' | 'parts_cost' | 'labor_hours';

const normalizeReason = (reason?: string): string =>
    (reason ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/** Modal body is selected from `simData.adjustment_reason`, not line type. */
export const resolveAdjustmentContentKind = (reason?: string): AdjustmentContentKind | null => {
    const value = normalizeReason(reason);
    if (!value) return null;
    if (value.includes('part not covered') || value.includes('labor not covered')) return 'conversation';
    if (value.includes('msrp 5% reduction') || value.includes('msrp cap')) return 'parts_cost';
    if (value.includes('claimed hours greater than srt')) return 'labor_hours';
    return null;
};

/** Sim-check mapping: adjustmentType -> clicked columnName -> content. */
const SIM_CHECK_COLUMN_CONTENT_KIND: Record<string, Record<string, AdjustmentContentKind>> = {
    part: { adjusted: 'parts_cost', status: 'conversation' },
    labor: { 'adj hrs': 'labor_hours', status: 'conversation' },
};

/**
 * Resolves the modal body for a clicked adjustment link. Sim-check clicks are resolved
 * from the line's adjustmentType and the clicked columnName first, falling back to
 * `simData.adjustment_reason` when the column is unmapped; other clicks use the reason only.
 */
export const resolveAdjustmentEventContentKind = (event: AdjustmentLinkEvent | null | undefined): AdjustmentContentKind | null => {
    if (event?.isSimCheck) {
        const adjustmentType = normalizeReason(event.data?.adjustmentType);
        const columnName = normalizeReason(event.columnName);
        const byColumn = SIM_CHECK_COLUMN_CONTENT_KIND[adjustmentType]?.[columnName];
        if (byColumn) return byColumn;
    }
    const reason = event?.data?.simData?.adjustment_reason;
    return resolveAdjustmentContentKind(typeof reason === 'string' ? reason : '');
};

export const getCoverageValidationNode = (requestExp: Record<string, unknown> | null) =>
    pickExplanation(findKey(requestExp, 'coverage_validation'));

export const getPartsValidationNode = (requestExp: Record<string, unknown> | null) =>
    pickExplanation(findKey(getCoverageValidationNode(requestExp), 'parts_validation'));

export const getLaborValidationNode = (requestExp: Record<string, unknown> | null) =>
    pickExplanation(findKey(getCoverageValidationNode(requestExp), 'labor_validation'));

export const getCostValidationNode = (requestExp: Record<string, unknown> | null) =>
    pickExplanation(findKey(requestExp, 'cost_validation'));

export const extractConversationLink = (node: Record<string, unknown> | null): string | null => {
    if (!node) return null;
    const raw = findKey(node, 'conversation@link', 'conversation');
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
};

export const parseConversationLink = (
    url: string
): { senderId: string; advisorId: string; messageId?: string } | null => {
    try {
        const parsed = new URL(url, window.location.origin);
        const parts = parsed.pathname.split('/').filter(Boolean);
        const convIndex = parts.findIndex((p) => p.toLowerCase() === 'conversation');
        if (convIndex < 0 || parts.length < convIndex + 3) return null;
        const decode = (value: string) => {
            try {
                return window.atob(value);
            } catch {
                return value;
            }
        };
        const senderId = decode(parts[convIndex + 1]);
        const advisorId = decode(parts[convIndex + 2]);
        const messageId = parts[convIndex + 3] ? decode(parts[convIndex + 3]) : undefined;
        if (!senderId || !advisorId) return null;
        return { senderId, advisorId, messageId };
    } catch {
        return null;
    }
};

export const wrapV2Subtree = (
    key: string,
    node: unknown
): ActionOutputV2Payload | null => {
    if (node == null) return null;
    const rec = asRecord(node);
    return {
        version: 2,
        score: typeof rec?.score === 'number' ? rec.score : undefined,
        order: [key],
        explanation: { [key]: node },
    };
};

const pickLineNode = (collapseNode: Record<string, unknown> | null, detailId: string | number | undefined) => {
    if (!collapseNode) return null;
    if (detailId == null) return collapseNode;
    return findKey(collapseNode, String(detailId)) ?? null;
};

export const extractCoverageConversationUrl = (
    actionOutput: unknown,
    failureId: string | number | undefined,
    _detailId?: string | number
): string | null => {
    const payload = getActionOutputV2Payload(actionOutput);
    const request = getRequestExplanation(payload, failureId);
    return (
        extractConversationLink(getLaborValidationNode(request)) ??
        extractConversationLink(getPartsValidationNode(request))
    );
};

/** @deprecated use extractCoverageConversationUrl */
export const extractPartConversationUrl = extractCoverageConversationUrl;

const extractCostSectionPayload = (
    actionOutput: unknown,
    failureId: string | number | undefined,
    detailId: string | number | undefined,
    sectionKeys: [string, string],
    wrapKey: string
): ActionOutputV2Payload | null => {
    const payload = getActionOutputV2Payload(actionOutput);
    const request = getRequestExplanation(payload, failureId);
    const cost = getCostValidationNode(request);
    const section = pickExplanation(findKey(cost, ...sectionKeys));
    const line = pickLineNode(section, detailId);
    if (line) return wrapV2Subtree(wrapKey, line);
    return section ? wrapV2Subtree(wrapKey, section) : null;
};

export const extractPartsCostValidationPayload = (
    actionOutput: unknown,
    failureId: string | number | undefined,
    detailId?: string | number
): ActionOutputV2Payload | null =>
    extractCostSectionPayload(
        actionOutput,
        failureId,
        detailId,
        ['parts_cost_validation', 'parts_cost_validation@collapse'],
        'parts_cost_validation'
    );

export const extractLaborHoursCheckPayload = (
    actionOutput: unknown,
    failureId: string | number | undefined,
    detailId?: string | number
): ActionOutputV2Payload | null =>
    extractCostSectionPayload(
        actionOutput,
        failureId,
        detailId,
        ['labor_hours_check', 'labor_hours_check@collapse'],
        'labor_hours_check'
    );

/** Sim-check fallback: the validation section carried inline on the clicked line's simData. */
export const extractSimDataSection = (
    simData: Record<string, unknown> | null | undefined,
    sectionKey: string
): ActionOutputV2Payload | null => {
    const section = pickExplanation(findKey(simData ?? null, sectionKey));
    return section ? wrapV2Subtree(sectionKey, section) : null;
};

/** @deprecated use extractLaborHoursCheckPayload */
export const extractLaborCostValidationPayload = extractLaborHoursCheckPayload;
